import { icons } from '../assets/icons';
import { GALLERY_ITEMS } from '../config/project-info';

export class TerminalGalleryComponent {
  private activeId: string = GALLERY_ITEMS[0].id;

  public render(): string {
    const activeItem = GALLERY_ITEMS.find(item => item.id === this.activeId) || GALLERY_ITEMS[0];

    return `
      <section id="gallery" class="py-16 md:py-24 border-t border-terminal-border bg-terminal-bg/80">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div class="text-center max-w-3xl mx-auto space-y-3 mb-12">
            <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-semibold bg-cyber-950/80 text-cyber-300 border border-cyber-800 shadow-glow-cyan">
              <span>// VISUAL SHOWCASE</span>
            </div>
            <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Interactive TUI Showcase
            </h2>
            <p class="text-sm sm:text-base text-terminal-muted font-mono">
              Explore Mastui's multi-column views, media rendering, and theme options in action.
            </p>
          </div>

          <!-- Tabs Selector -->
          <div class="flex flex-wrap items-center justify-center gap-2 mb-8">
            ${GALLERY_ITEMS.map(item => `
              <button 
                data-gallery-tab="${item.id}"
                class="gallery-tab-btn px-4 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer ${
                  this.activeId === item.id 
                    ? 'bg-matrix-500 text-terminal-bgDark shadow-glow' 
                    : 'bg-terminal-card text-terminal-muted hover:text-terminal-fg border border-terminal-border hover:border-matrix-500/50'
                }"
              >
                <span>${item.title}</span>
              </button>
            `).join('')}
          </div>

          <!-- Main Display Card -->
          <div class="max-w-5xl mx-auto rounded-2xl bg-terminal-card border border-terminal-border shadow-glow-lg overflow-hidden">
            
            <!-- Window Bar -->
            <div class="flex items-center justify-between px-4 py-3 bg-terminal-bgDark border-b border-terminal-border">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-rose-500/80"></div>
                <div class="w-3 h-3 rounded-full bg-amber-500/80"></div>
                <div class="w-3 h-3 rounded-full bg-matrix-500/80"></div>
                <span id="gallery-window-title" class="ml-2 text-xs font-mono text-matrix-400">mastui :: ${activeItem.title}</span>
              </div>
              <span id="gallery-window-badge" class="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyber-950 text-cyber-300 border border-cyber-700">
                ${activeItem.badge}
              </span>
            </div>

            <!-- Image Container -->
            <div class="p-4 sm:p-6 bg-terminal-bgDark/95 space-y-4">
              <div class="relative rounded-xl overflow-hidden border border-matrix-900 bg-black flex items-center justify-center min-h-[300px]">
                <img 
                  id="gallery-main-img"
                  src="${activeItem.image}" 
                  alt="${activeItem.title}" 
                  class="w-full h-auto max-h-[560px] object-contain transition-opacity duration-200"
                  loading="lazy"
                />
              </div>

              <!-- Description Box -->
              <div class="p-4 rounded-xl bg-terminal-card border border-terminal-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div class="space-y-1">
                  <h4 id="gallery-desc-title" class="text-sm font-bold text-matrix-400 font-mono">
                    ${activeItem.title}
                  </h4>
                  <p id="gallery-desc-text" class="text-xs text-terminal-fg/90 font-mono leading-relaxed max-w-3xl">
                    ${activeItem.description}
                  </p>
                </div>

                <a 
                  id="gallery-external-link"
                  href="${activeItem.image}" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono text-cyber-300 bg-cyber-950 hover:bg-cyber-900 border border-cyber-700 transition-colors whitespace-nowrap self-start sm:self-auto"
                >
                  <span>Full Image</span>
                  ${icons.externalLink('w-3 h-3')}
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>
    `;
  }

  public bindEvents() {
    const tabButtons = document.querySelectorAll('.gallery-tab-btn');
    const mainImg = document.getElementById('gallery-main-img') as HTMLImageElement;
    const windowTitle = document.getElementById('gallery-window-title');
    const windowBadge = document.getElementById('gallery-window-badge');
    const descTitle = document.getElementById('gallery-desc-title');
    const descText = document.getElementById('gallery-desc-text');
    const externalLink = document.getElementById('gallery-external-link') as HTMLAnchorElement;

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-gallery-tab');
        if (!id) return;

        const item = GALLERY_ITEMS.find(i => i.id === id);
        if (!item) return;

        this.activeId = id;

        // Update tab styles
        tabButtons.forEach(b => {
          b.className = 'gallery-tab-btn px-4 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer bg-terminal-card text-terminal-muted hover:text-terminal-fg border border-terminal-border hover:border-matrix-500/50';
        });
        btn.className = 'gallery-tab-btn px-4 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer bg-matrix-500 text-terminal-bgDark shadow-glow';

        // Update content with subtle fade
        if (mainImg) {
          mainImg.style.opacity = '0.5';
          mainImg.src = item.image;
          mainImg.alt = item.title;
          setTimeout(() => {
            mainImg.style.opacity = '1';
          }, 150);
        }

        if (windowTitle) windowTitle.textContent = `mastui :: ${item.title}`;
        if (windowBadge) windowBadge.textContent = item.badge;
        if (descTitle) descTitle.textContent = item.title;
        if (descText) descText.textContent = item.description;
        if (externalLink) externalLink.href = item.image;
      });
    });
  }
}
